import React from 'react';
import { useDispatch } from 'react-redux';
import SessionImportButton from './SessionImportButton';
import SessionHistoryButton from './SessionHistoryButton';
import { buildSessionImportConfigs } from './sessionImportConfigs';
import { useFile, usePreProcessing, useResult } from '../../contexts/index.jsx';

/**
 * @function SelectionSessionImportButton
 * @description Generic "import any module's session" button for the module-selection page. Reads
 * the file's own `module` field (rather than validating against one fixed module, the way every
 * other `SessionImportButton` usage does) and routes it to that module's own `importSessionState`
 * + result page. Split out from `NavBar` itself because it depends on `useResult`/`useFile`/
 * `usePreProcessing` (needed only by bathymetry's importer), which are only available inside
 * `AppContexts` — `NavBar` is also reused on the public marketing pages (`/`, `/faq`, `/problems`)
 * that render outside it, so those hooks can't be called there. Rendering this component only
 * conditionally (on the selection page) keeps `NavBar` itself hook-safe on every other page.
 * @see {@link buildSessionImportConfigs}
 * **Called by:
 * @see {NavBar} - only when the current route is the module-selection page.
 * @memberof module:Core
 * @returns {JSX.Element}
 */
const SelectionSessionImportButton = () => {
  const dispatch = useDispatch();
  const { setData } = useResult();
  const { setFileData } = useFile();
  const {
    saveProcessingCollectionValues,
    waterMaskMethodRef,
    generateCollection,
    cleanAndMaskImages,
    createMosaicByCommonId,
    collectionRef,
  } = usePreProcessing();

  const handleImport = async (parsed) => {
    const importConfigs = buildSessionImportConfigs({
      dispatch,
      setData,
      setFileData,
      saveProcessingCollectionValues,
      waterMaskMethodRef,
      generateCollection,
      cleanAndMaskImages,
      createMosaicByCommonId,
      collectionRef,
    });
    const config = importConfigs[parsed.module];
    if (!config) {
      throw new Error(`Unsupported session module: ${parsed.module}`);
    }
    await config.onImport(parsed);
  };

  return (
    <React.Fragment>
      <SessionHistoryButton onOpen={handleImport} />
      <SessionImportButton onImport={handleImport} />
    </React.Fragment>
  );
};

export default SelectionSessionImportButton;
